import { create } from 'zustand';
import _ from 'lodash';
import NotificationService from '~/modules/services/notification-service';
import { captureError } from '~/modules/pixwel/exception-handler.factory';

const PAGE_SIZE = 70;

const DEFAULT_NOTIFICATION_TYPE = {
	data: [],
	isLoadingNew: false,
	pagination: {
		isLoadingMore: false,
		hasMore: true
	}
};

// ─── Standalone selectors (use with useNotificationsStore(selector)) ───

export function selectNotifications(state) {
	let type = state.filters.type || 'workRequest';
	let data = _.get(state, ['notificationsByType', type, 'data'], []);
	return (state.filters.read === 0) ? data.filter((n) => !n.read) : data;
}

export function selectSummary(state) {
	return state.summary;
}

export function selectIsLoadingNew(state) {
	let type = state.filters.type || 'workRequest';
	return _.get(state, ['notificationsByType', type, 'isLoadingNew'], false);
}

export function selectIsLoadingMore(state) {
	let type = state.filters.type || 'workRequest';
	return _.get(state, ['notificationsByType', type, 'pagination', 'isLoadingMore'], false);
}

export function selectHasMore(state) {
	let type = state.filters.type || 'workRequest';
	return _.get(state, ['notificationsByType', type, 'pagination', 'hasMore'], true);
}

export function selectHasUnread(state) {
	return ['workRequest', 'embargo', 'other']
		.map((type) => _.get(state, ['summary', type, 'unread'], 0))
		.some((num) => num > 0);
}

export function selectIsUpdating(state) {
	return state.isUpdating;
}

export function selectIsError(state) {
	return state.isError;
}

const useNotificationsStore = create((set, get) => ({
	isError: false,
	isUpdating: false,
	summary: {},
	notificationsByType: {},
	filters: {
		type: 'workRequest',
		q: '',
		read: 0
	},

	// ─── Actions ─────────────────────────────────────────────────

	manualUpdateNotifications: ({ type, notification }) => {
		set((state) => {
			let data = [
				notification,
				..._.get(state, ['notificationsByType', type, 'data'], [])
			];

			let oldSummary = _.get(state, ['summary'], {});
			let summary = _.clone(oldSummary);

			if(oldSummary[type]) {
				summary[type] = { ...oldSummary[type], unread: oldSummary[type].unread + 1 };
			}

			return {
				summary,
				notificationsByType: {
					...state.notificationsByType,
					[type]: {
						...state.notificationsByType[type],
						data
					}
				}
			};
		});
	},

	fetchNewNotifications: async({ filters }) => {
		const state = get();

		// Check if we're already fetching new notifications for this type
		let isLoadingNew = _.get(state, ['notificationsByType', filters.type, 'isLoadingNew'], false);
		if(isLoadingNew) {
			return;
		}

		// Get pagination info
		let pagination = _.get(state, ['notificationsByType', filters.type, 'pagination'], null);
		let end = PAGE_SIZE;

		if(pagination) {
			end = pagination.newCount || PAGE_SIZE;
		}

		// Set loading state
		set((prev) => ({
			...prev,
			filters,
			isError: false,
			notificationsByType: {
				...prev.notificationsByType,
				[filters.type]: {
					...prev.notificationsByType[filters.type],
					isLoadingNew: true,
					pagination: {
						..._.get(prev, ['notificationsByType', filters.type, 'pagination'], {}),
						isLoadingMore: false
					}
				}
			}
		}));

		try {
			let summary = await NotificationService.getSummary();
			let pagedResults = await NotificationService.getPagedNotifications(filters, { limit: end });
			let { results: notifications, pagination } = pagedResults;

			set((prev) => ({
				...prev,
				filters,
				summary,
				notificationsByType: {
					...prev.notificationsByType,
					[filters.type]: {
						data: [...notifications],
						isLoadingNew: false,
						pagination: {
							isLoadingMore: false,
							hasMore: (pagination.page + 1) * PAGE_SIZE < pagination.total,
							currentPage: pagination.page,
							total: pagination.total
						}
					}
				}
			}));
		}
		catch(e) {
			captureError(e);
			set((prev) => ({
				...prev,
				isError: true,
				notificationsByType: {
					...prev.notificationsByType,
					[filters.type]: {
						...prev.notificationsByType[filters.type],
						isLoadingNew: false,
						data: []
					}
				}
			}));
		}
	},

	fetchMoreNotifications: async({ filters }) => {
		const state = get();

		// Check if we're already fetching
		let isLoadingNew = _.get(state, ['notificationsByType', filters.type, 'isLoadingNew'], false);
		let isLoadingMore = _.get(state, ['notificationsByType', filters.type, 'pagination', 'isLoadingMore'], false);
		if(isLoadingNew || isLoadingMore) {
			return;
		}

		let currentPage = _.get(state, ['notificationsByType', filters.type, 'pagination', 'currentPage'], -1);

		// Set loading state
		set((prev) => ({
			...prev,
			filters,
			notificationsByType: {
				...prev.notificationsByType,
				[filters.type]: {
					...prev.notificationsByType[filters.type],
					isLoadingNew: false,
					pagination: {
						..._.get(prev, ['notificationsByType', filters.type, 'pagination'], {}),
						isLoadingMore: true
					}
				}
			}
		}));

		try {
			let summary = await NotificationService.getSummary();

			if(!summary[filters.type]) {
				set((prev) => ({
					...prev,
					notificationsByType: {
						...prev.notificationsByType,
						[filters.type]: {
							...prev.notificationsByType[filters.type],
							isLoadingNew: false,
							pagination: {
								..._.get(prev, ['notificationsByType', filters.type, 'pagination'], {}),
								isLoadingMore: false
							}
						}
					}
				}));
				return;
			}

			let pagedResults = await NotificationService.getPagedNotifications(filters, {
				page: currentPage + 1,
				limit: PAGE_SIZE
			});

			let { results: notifications, pagination } = pagedResults;

			set((prev) => {
				let oldNotifications = _.get(prev, ['notificationsByType', filters.type, 'data'], []);

				return {
					...prev,
					filters,
					summary,
					notificationsByType: {
						...prev.notificationsByType,
						[filters.type]: {
							data: [...oldNotifications, ...notifications],
							isLoadingNew: false,
							pagination: {
								isLoadingMore: false,
								hasMore: (pagination.page + 1) * PAGE_SIZE < pagination.total,
								currentPage: pagination.page,
								total: pagination.total
							}
						}
					}
				};
			});
		}
		catch(e) {
			captureError(e);
			set((prev) => {
				let container = _.get(prev, ['notificationsByType', filters.type], DEFAULT_NOTIFICATION_TYPE);

				return {
					...prev,
					isError: true,
					notificationsByType: {
						...prev.notificationsByType,
						[filters.type]: {
							...container,
							isLoadingNew: false,
							data: [],
							pagination: {
								..._.get(container, 'pagination', {}),
								isLoadingMore: false
							}
						}
					}
				};
			});
		}
	},

	markRead: async({ type, notification }) => {
		// Optimistic update
		set((prev) => {
			let data = _.get(prev, ['notificationsByType', type, 'data'], []).map((n) => {
				if(n._id === notification._id) {
					return { ...n, read: true, viewed: true };
				}
				return n;
			});

			let oldSummary = _.get(prev, ['summary'], {});
			let newSummary = _.cloneDeep(oldSummary);
			if(newSummary[type]) {
				newSummary[type].unread = Math.max(0, (newSummary[type].unread || 0) - 1);
			}

			return {
				summary: newSummary,
				notificationsByType: {
					...prev.notificationsByType,
					[type]: {
						...prev.notificationsByType[type],
						data
					}
				}
			};
		});

		try {
			await NotificationService.markRead(notification._id);
		}
		catch(e) {
			captureError(e);
			// Rollback
			set((prev) => {
				let data = _.get(prev, ['notificationsByType', type, 'data'], []).map((n) => {
					if(n._id === notification._id) {
						return { ...n, read: false, viewed: false };
					}
					return n;
				});

				let oldSummary = _.get(prev, ['summary'], {});
				let newSummary = _.cloneDeep(oldSummary);
				if(newSummary[type]) {
					newSummary[type].unread = (newSummary[type].unread || 0) + 1;
				}

				return {
					summary: newSummary,
					notificationsByType: {
						...prev.notificationsByType,
						[type]: {
							...prev.notificationsByType[type],
							data
						}
					}
				};
			});
		}
	},

	markUnread: async({ type, notification }) => {
		// Optimistic update
		set((prev) => {
			let data = _.get(prev, ['notificationsByType', type, 'data'], []).map((n) => {
				if(n._id === notification._id) {
					return { ...n, read: false, viewed: false };
				}
				return n;
			});

			let oldSummary = _.get(prev, ['summary'], {});
			let newSummary = _.cloneDeep(oldSummary);
			if(newSummary[type]) {
				newSummary[type].unread = (newSummary[type].unread || 0) + 1;
			}

			return {
				summary: newSummary,
				notificationsByType: {
					...prev.notificationsByType,
					[type]: {
						...prev.notificationsByType[type],
						data
					}
				}
			};
		});

		try {
			await NotificationService.markUnread(notification._id);
		}
		catch(e) {
			captureError(e);
			// Rollback
			set((prev) => {
				let data = _.get(prev, ['notificationsByType', type, 'data'], []).map((n) => {
					if(n._id === notification._id) {
						return { ...n, read: true, viewed: true };
					}
					return n;
				});

				let oldSummary = _.get(prev, ['summary'], {});
				let newSummary = _.cloneDeep(oldSummary);
				if(newSummary[type]) {
					newSummary[type].unread = Math.max(0, (newSummary[type].unread || 0) - 1);
				}

				return {
					summary: newSummary,
					notificationsByType: {
						...prev.notificationsByType,
						[type]: {
							...prev.notificationsByType[type],
							data
						}
					}
				};
			});
		}
	},

	markAllRead: async({ type }) => {
		const state = get();
		let oldUnread = _.get(state, ['summary', type, 'unread'], 0);

		// Optimistic update
		set((prev) => {
			let data = _.get(prev, ['notificationsByType', type, 'data'], []).map((n) => ({
				...n,
				read: true
			}));

			let oldSummary = _.get(prev, ['summary'], {});
			let newSummary = _.cloneDeep(oldSummary);
			if(newSummary[type]) {
				newSummary[type].unread = 0;
			}

			return {
				isUpdating: true,
				summary: newSummary,
				notificationsByType: {
					...prev.notificationsByType,
					[type]: {
						...prev.notificationsByType[type],
						data
					}
				}
			};
		});

		try {
			await NotificationService.markAllRead(type);
			set({ isUpdating: false });
		}
		catch(e) {
			captureError(e);
			// Rollback
			set((prev) => {
				let oldSummary = _.get(prev, ['summary'], {});
				let newSummary = _.cloneDeep(oldSummary);
				if(newSummary[type]) {
					newSummary[type].unread = oldUnread;
				}

				return {
					isError: true,
					isUpdating: false,
					summary: newSummary
				};
			});
		}
	}
}));

export default useNotificationsStore;

